<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  translationKey: {
    type: String,
    required: true,
  },
  entity: {
    type: Object,
    required: true,
  },
  deletePayload: {
    type: Object,
    default: null,
  },
  description: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['deleteSuccess']);

const { t } = useI18n();
const store = useStore();
const deleteDialogRef = ref(null);
const i18nKey = computed(() => {
  return props.translationKey || props.type.toUpperCase();
});

const deleteCopy = computed(() => {
  if (i18nKey.value === 'ASSISTANTS') {
    return {
      title: t('CAPTAIN.ASSISTANTS.DELETE.TITLE'),
      description: t('CAPTAIN.ASSISTANTS.DELETE.DESCRIPTION'),
      confirm: t('CAPTAIN.ASSISTANTS.DELETE.CONFIRM'),
      success: t('CAPTAIN.ASSISTANTS.DELETE.SUCCESS_MESSAGE'),
      error: t('CAPTAIN.ASSISTANTS.DELETE.ERROR_MESSAGE'),
    };
  }
  if (i18nKey.value === 'DOCUMENTS') {
    return {
      title: t('CAPTAIN.DOCUMENTS.DELETE.TITLE'),
      description: t('CAPTAIN.DOCUMENTS.DELETE.DESCRIPTION'),
      confirm: t('CAPTAIN.DOCUMENTS.DELETE.CONFIRM'),
      success: t('CAPTAIN.DOCUMENTS.DELETE.SUCCESS_MESSAGE'),
      error: t('CAPTAIN.DOCUMENTS.DELETE.ERROR_MESSAGE'),
    };
  }
  if (i18nKey.value === 'INBOXES') {
    return {
      title: t('CAPTAIN.INBOXES.DELETE.TITLE'),
      description: t('CAPTAIN.INBOXES.DELETE.DESCRIPTION'),
      confirm: t('CAPTAIN.INBOXES.DELETE.CONFIRM'),
      success: t('CAPTAIN.INBOXES.DELETE.SUCCESS_MESSAGE'),
      error: t('CAPTAIN.INBOXES.DELETE.ERROR_MESSAGE'),
    };
  }
  if (i18nKey.value === 'CUSTOM_TOOLS') {
    return {
      title: t('CAPTAIN.CUSTOM_TOOLS.DELETE.TITLE'),
      description: t('CAPTAIN.CUSTOM_TOOLS.DELETE.DESCRIPTION'),
      confirm: t('CAPTAIN.CUSTOM_TOOLS.DELETE.CONFIRM'),
      success: t('CAPTAIN.CUSTOM_TOOLS.DELETE.SUCCESS_MESSAGE'),
      error: t('CAPTAIN.CUSTOM_TOOLS.DELETE.ERROR_MESSAGE'),
    };
  }
  return {
    title: t('CAPTAIN.RESPONSES.DELETE.TITLE'),
    description: t('CAPTAIN.RESPONSES.DELETE.DESCRIPTION'),
    confirm: t('CAPTAIN.RESPONSES.DELETE.CONFIRM'),
    success: t('CAPTAIN.RESPONSES.DELETE.SUCCESS_MESSAGE'),
    error: t('CAPTAIN.RESPONSES.DELETE.ERROR_MESSAGE'),
  };
});

const deleteEntity = async payload => {
  if (!payload) return;

  try {
    await store.dispatch(`captain${props.type}/delete`, payload);
    emit('deleteSuccess');
    useAlert(deleteCopy.value.success);
  } catch (error) {
    useAlert(deleteCopy.value.error);
  }
};

const handleDialogConfirm = async () => {
  await deleteEntity(props.deletePayload || props.entity.id);
  deleteDialogRef.value?.close();
};

defineExpose({ dialogRef: deleteDialogRef });
</script>

<template>
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="deleteCopy.title"
    :description="description || deleteCopy.description"
    :confirm-button-label="deleteCopy.confirm"
    @confirm="handleDialogConfirm"
  />
</template>
